import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { formatter } from '@autopass-web/domain';
import { MochiInput } from '@autopass-web/mochi-component';
import {
  creditCardCvcStrategy,
  creditCardExpireDateStrategy,
  creditCardNumberStrategy,
} from '@autopass-web/mochi-utils';

import { Block } from '../../../components/block';
import { useImperativeForm } from '../../../hooks/use-bind-async-submit';
import styles from './credit-card.module.scss';

import type { FormControlRef } from '../../../hooks/use-bind-async-submit';
import type { PaymentMethodInfo } from '@autopass-web/mochi-payment-module';
import type { RefObject } from 'react';

export type CreditCardForm = {
  number: string;
  exp: string;
  cvc: string;
};

export type ControlRef = FormControlRef<PaymentMethodInfo>;

const creditCardSchema = z.object({
  number: z
    .string()
    .min(1, {
      message: '此欄位為必填',
    })
    .refine(creditCardNumberStrategy, {
      message: '格式錯誤',
    }),
  exp: z
    .string()
    .min(1, {
      message: '此欄位為必填',
    })
    .refine(creditCardExpireDateStrategy, {
      message: '格式錯誤',
    }),
  cvc: z
    .string()
    .min(1, {
      message: '此欄位為必填',
    })
    .refine(creditCardCvcStrategy, {
      message: '格式錯誤',
    }),
});

export const CreditCard = ({
  controlRef,
  defaultValues,
}: {
  controlRef: RefObject<ControlRef>;
  defaultValues?: Partial<CreditCardForm>;
}) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
    setFocus,
  } = useForm<CreditCardForm>({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(creditCardSchema),
  });

  const formProps = useImperativeForm(controlRef, errors, (submit) => {
    return handleSubmit((data) =>
      submit({ paymentMethod: 'credit_card', paymentInfo: data }),
    );
  });

  return (
    <Block title="付款資訊">
      <form {...formProps}>
        <>
          <MochiInput
            autoComplete="cc-number"
            maxLength={19}
            variant="grey"
            placeholder="信用卡/金融卡號碼"
            {...register('number', {
              onChange: (e) => {
                const value = e.target.value;
                const formattedValue = formatter(value, ' ', 4);
                setValue('number', formattedValue);

                errors?.number && trigger('number');
                value.length === 19 && setFocus('exp');
              },
            })}
            errorMessage={errors?.number?.message}
          />
          <div className={styles['row']}>
            <MochiInput
              autoComplete="cc-exp"
              variant="grey"
              inputMode="numeric"
              maxLength={5}
              placeholder="有效日期 MM/YY"
              {...register('exp', {
                onChange: (e) => {
                  const value = e.target.value;
                  const formattedValue = formatter(value, '/', 2);
                  setValue('exp', formattedValue);

                  errors?.exp && trigger('exp');
                  value.length === 5 && setFocus('cvc');
                },
              })}
              errorMessage={errors?.exp?.message}
            />
            <MochiInput
              autoComplete="cc-csc"
              variant="grey"
              inputMode="numeric"
              maxLength={3}
              placeholder="安全碼"
              {...register('cvc', {
                onChange: () => {
                  if (errors?.cvc) {
                    trigger('cvc');
                  }
                },
              })}
              errorMessage={errors?.cvc?.message}
            />
          </div>
        </>
      </form>
    </Block>
  );
};
